import { useState, useCallback, useEffect, useMemo } from 'react';
import { PlusIcon, ArrowsOutIcon, ArrowsInIcon, PencilSimpleIcon, PushPinIcon, InfoIcon, MapPinIcon } from '@phosphor-icons/react';
import { SortableList, Button, useSortable, FilterChipGroup, Notice, EmptyState, Chip } from '@origo/origo-ui';
import { getSectionPosition } from '@origo/seksjon';
import type { Seksjon, SeksjonFormValues } from '../types';
import { SectionItem } from './SectionItem';
import { SectionEditorPage } from './SectionEditorPage';

const nf = new Intl.NumberFormat('nb-NO');

/** Punktet innbyggeren faktisk ser: synlig her, ikke skjult lokalt eller av et
 *  overordnet nivå. Samme sannhet som innbyggervisningen. */
function isVisibleToCitizen(s: Seksjon): boolean {
    return (
        !(s.hideObjectId != null || !s.synlig) &&
        !s.isHiddenByAncestor &&
        !s.isHiddenAtThisLevel
    );
}
import styles from './SectionList.module.css';

interface BreadcrumbItem {
    label: string;
    onClick: () => void;
}

interface SectionListProps {
    seksjoner: Seksjon[];
    contextCode?: string;
    contextLabel: string;
    /** Vertikalnivå vist som etikett ved tittelen, når det ikke er «på bakken». */
    contextVertikalnivå?: string;
    contextSubtitle: string;
    breadcrumb?: BreadcrumbItem[];
    flatView?: boolean;
    areaChildCount?: number;
    onAdd?: (values: SeksjonFormValues) => Promise<string>;
    onUpdate: (existing: Seksjon, values: SeksjonFormValues) => Promise<void>;
    onDelete: (id: string) => void;
    onToggleOverride?: (globalId: string, isInherited: boolean, override: boolean) => void;
    onHide?: (globalId: string) => void;
    onUnhide?: (hideObjectId: number) => void;
    /** Persistér ny rekkefølge av middle-tier seksjoner. Mottar full ID-liste
        i ønsket rekkefølge inkludert top og bottom (de står faste, men sendes
        med for at backend skal få komplett prioritetsoppdatering). */
    onReorder?: (newOrder: string[]) => void;
    /** Hurtigvalg: opprett ny delområdegruppe innenfor aktiv delområdetype. */
    onAddGroup?: () => void;
    /** Hurtigvalg: opprett nytt egendefinert område nestet under aktivt delområde/område. */
    onAddOmråde?: () => void;
    /** Hurtigvalg: rediger aktivt egendefinert område / delområdegruppe. */
    onEdit?: () => void;
    /** Åpne eiendomsvelgeren for det geografiske nivået (plan/delområde/gruppe). */
    onEditEiendommer?: () => void;
    /** Sant mens seksjoner hentes — undertrykker tom-tilstanden så den ikke
        blinker under kontekst-bytte før data er på plass. */
    loading?: boolean;
    isPublished?: boolean;
    onPublishAfterSave?: () => Promise<void>;
    importedGlobalIds?: ReadonlySet<string>;
    /** Kommuneplan/kommunedelplan: punkter kan merkes «kun uregulerte» (#15236). */
    kanMerkeUregulert?: boolean;
}

/** undefined = lukket, null = ny seksjon, Seksjon = redigering */
type EditorState = Seksjon | null | undefined;

export function SectionList({
    seksjoner,
    contextCode,
    contextLabel,
    contextVertikalnivå,
    contextSubtitle,
    breadcrumb,
    onAdd,
    onUpdate,
    onDelete,
    onToggleOverride,
    onHide,
    onUnhide,
    onReorder,
    onAddGroup,
    onAddOmråde,
    onEdit,
    onEditEiendommer,
    loading,
    isPublished,
    onPublishAfterSave,
    importedGlobalIds,
    kanMerkeUregulert,
}: Readonly<SectionListProps>) {
    const [editing, setEditing] = useState<EditorState>(undefined);

    // Linse — én «hva ser jeg på»-akse som forener opprinnelsesfilter + visning:
    //   alle      = alt (redigering)
    //   her       = kun punkter opprettet på dette nivået (redigering, filtrert; WI 12661)
    //   innbygger = read-only, kun det innbyggeren faktisk ser
    const [lens, setLens] = useState<'alle' | 'her' | 'innbygger'>('alle');

    const totalCount = seksjoner.length;
    const herCount = useMemo(
        () => seksjoner.filter((s) => !s.isInherited).length,
        [seksjoner]
    );
    // Filteret gir bare mening når noe faktisk er arvet — ellers er «Alle» og
    // «Opprettet her» identiske og raden blir støy.
    const hasInherited = herCount < totalCount;
    // «Opprettet her»-linsen finnes bare når noe er arvet; ellers er den identisk
    // med «alle». Fall tilbake til «alle» så vi aldri filtrerer i det skjulte.
    const effectiveLens = lens === 'her' && !hasInherited ? 'alle' : lens;
    const dragEnabled = effectiveLens === 'alle';

    // Nullstill linsen når man navigerer til en annen kontekst.
    useEffect(() => {
        setLens('alle');
    }, [contextLabel]);

    // Tier-splitt via sentral plasserings-helper:
    //  - top:    kommunal + topp ∈ {true, undefined}
    //  - middle: ikke-kommunal — eneste gruppen som kan dras
    //  - bottom: kommunal + topp === false
    const { topItems, middleItems, bottomItems } = useMemo(() => {
        const visible =
            effectiveLens === 'her'
                ? seksjoner.filter((s) => !s.isInherited)
                : effectiveLens === 'innbygger'
                  ? seksjoner.filter(isVisibleToCitizen)
                  : seksjoner;
        const top: Seksjon[] = [];
        const middle: Seksjon[] = [];
        const bottom: Seksjon[] = [];
        for (const s of visible) {
            const pos = getSectionPosition(s);
            if (pos === 'top') top.push(s);
            else if (pos === 'bottom') bottom.push(s);
            else middle.push(s);
        }
        return { topItems: top, middleItems: middle, bottomItems: bottom };
    }, [seksjoner, effectiveLens]);

    const allSorted = useMemo(
        () => [...topItems, ...middleItems, ...bottomItems],
        [topItems, middleItems, bottomItems]
    );

    // Antall punkter innbyggeren ser — driver telleren på «Slik ser innbyggeren
    // det»-linsen og hvor mange som er skjult. Uavhengig av valgt linse.
    const citizenVisibleCount = useMemo(
        () => seksjoner.filter(isVisibleToCitizen).length,
        [seksjoner]
    );
    const citizenHiddenCount = totalCount - citizenVisibleCount;

    // Track expanded state per item (keyed by seksjon.objectId)
    const [expandedIds, setExpandedIds] = useState<Set<number>>(new Set());
    const allExpanded = allSorted.length > 0 && allSorted.every(s => expandedIds.has(s.objectId));

    const toggleAll = useCallback(() => {
        if (allExpanded) {
            setExpandedIds(new Set());
        } else {
            setExpandedIds(new Set(allSorted.map(s => s.objectId)));
        }
    }, [allExpanded, allSorted]);

    const handleExpandedChange = useCallback((id: number, expanded: boolean) => {
        setExpandedIds(prev => {
            const next = new Set(prev);
            if (expanded) {
                next.add(id);
            } else {
                next.delete(id);
            }
            return next;
        });
    }, []);

    // Hver tier reorderes uavhengig — dragging holder seg innen sin gruppe
    // (kommunale topp / ikke-kommunale / kommunale bunn). Bygger fullOrder
    // ved å splice riktig tier og beholde de to andre uendret.
    const buildFullOrder = useCallback(
        (tier: 'top' | 'middle' | 'bottom', fromIndex: number, toIndex: number): string[] => {
            const top = topItems.map((s) => String(s.objectId));
            const middle = middleItems.map((s) => String(s.objectId));
            const bottom = bottomItems.map((s) => String(s.objectId));
            const target = tier === 'top' ? top : tier === 'middle' ? middle : bottom;
            const [moved] = target.splice(fromIndex, 1);
            target.splice(toIndex, 0, moved);
            return [...top, ...middle, ...bottom];
        },
        [topItems, middleItems, bottomItems]
    );

    const topSortable = useSortable({
        count: topItems.length,
        onReorder: (fromIndex, toIndex) => {
            if (!onReorder || fromIndex === toIndex) return;
            onReorder(buildFullOrder('top', fromIndex, toIndex));
        },
    });

    const middleSortable = useSortable({
        count: middleItems.length,
        onReorder: (fromIndex, toIndex) => {
            if (!onReorder || fromIndex === toIndex) return;
            onReorder(buildFullOrder('middle', fromIndex, toIndex));
        },
    });

    const bottomSortable = useSortable({
        count: bottomItems.length,
        onReorder: (fromIndex, toIndex) => {
            if (!onReorder || fromIndex === toIndex) return;
            onReorder(buildFullOrder('bottom', fromIndex, toIndex));
        },
    });

    async function handleSave(values: SeksjonFormValues): Promise<string | undefined> {
        if (editing === null || editing === undefined) {
            if (!onAdd) return undefined;
            return await onAdd(values);
        } else {
            await onUpdate(editing, values);
            return undefined;
        }
    }

    // SectionItem trigger `onUpdate` for enkle felt-toggler (topp / infoboks)
    // med hele seksjonen som input. Adapter til det 2-argument-paret
    // (existing, values) som useAdminSections forventer.
    const handleItemUpdate = useCallback((seksjon: Seksjon) => {
        onUpdate(seksjon, {
            tittel: seksjon.tittel,
            tekst: seksjon.tekst,
            kommunal: seksjon.kommunal,
            topp: seksjon.topp,
            infoboks: Boolean(seksjon.infoboks),
            synlig: seksjon.synlig,
            kunUregulert: seksjon.kunUregulert,
        });
    }, [onUpdate]);

    return (
        <div className={styles.root}>
            {/* ---- Header ---- */}
            <div className={styles.header}>
                {breadcrumb && breadcrumb.length > 0 && (
                    <nav className={styles.breadcrumb} aria-label="Navigasjon">
                        {breadcrumb.map((item, i) => (
                            <button
                                key={i}
                                type="button"
                                className={styles.breadcrumbLink}
                                onClick={item.onClick}
                            >
                                {item.label}
                            </button>
                        ))}
                    </nav>
                )}

                <div className={styles.titleRow}>
                    {contextCode && (
                        <span className={styles.contextCode}>{contextCode}</span>
                    )}
                    <h2 className={styles.contextLabel}>{contextLabel}</h2>
                    {contextVertikalnivå && (
                        <Chip variant="info">{contextVertikalnivå}</Chip>
                    )}
                </div>

                <p className={styles.contextSubtitle}>{contextSubtitle}</p>

                <div className={styles.headerActions}>
                    {onAdd && (
                        <Button variant="secondary" onClick={() => setEditing(null)}>
                            <PlusIcon size={14} />
                            Legg til veiledningspunkt
                        </Button>
                    )}
                    {onAddGroup && (
                        <Button variant="secondary" onClick={onAddGroup}>
                            <PlusIcon size={14} />
                            Ny gruppe
                        </Button>
                    )}
                    {onAddOmråde && (
                        <Button variant="secondary" onClick={onAddOmråde}>
                            <PlusIcon size={14} />
                            Nytt egendefinert område
                        </Button>
                    )}
                    {onEdit && (
                        <Button variant="secondary" onClick={onEdit}>
                            <PencilSimpleIcon size={14} />
                            Rediger
                        </Button>
                    )}
                    {onEditEiendommer && (
                        <Button variant="secondary" onClick={onEditEiendommer}>
                            <MapPinIcon size={14} />
                            Rediger eiendommer
                        </Button>
                    )}
                    {allSorted.length > 0 && (
                        <Button variant="ghost" onClick={toggleAll}>
                            {allExpanded
                                ? <><ArrowsInIcon size={14} /> Minimer alle</>
                                : <><ArrowsOutIcon size={14} /> Ekspander alle</>
                            }
                        </Button>
                    )}
                </div>
            </div>

            {/* ---- Linse: forener opprinnelsesfilter + innbygger-visning ---- */}
            {totalCount > 0 && (
                <FilterChipGroup
                    label="Velg hvilke veiledningspunkt som vises"
                    value={effectiveLens}
                    onChange={(v) => setLens(v as 'alle' | 'her' | 'innbygger')}
                    options={[
                        { value: 'alle', label: 'Alle', count: totalCount },
                        ...(hasInherited
                            ? [{ value: 'her', label: 'Opprettet her', count: herCount }]
                            : []),
                        {
                            value: 'innbygger',
                            label: 'Slik ser innbyggeren det',
                            count: citizenVisibleCount,
                        },
                    ]}
                />
            )}

            {effectiveLens === 'innbygger' && (
                <Notice variant="info">
                    Slik ser innbyggeren planen nå.{' '}
                    {citizenHiddenCount > 0
                        ? `${nf.format(citizenHiddenCount)} ${citizenHiddenCount === 1 ? 'punkt' : 'punkter'} vises ikke.`
                        : 'Alle punktene vises.'}
                </Notice>
            )}

            {/* ---- List ---- */}
            <div className={styles.listContainer}>
                {!loading && allSorted.length === 0 && effectiveLens === 'her' ? (
                    <EmptyState
                        icon={<InfoIcon size={32} />}
                        title="Ingen veiledningspunkter på dette nivået"
                        action={
                            onAdd ? (
                                <Button variant="secondary" onClick={() => setEditing(null)}>
                                    <PlusIcon size={14} aria-hidden="true" />
                                    Legg til veiledning
                                </Button>
                            ) : undefined
                        }
                    />
                ) : !loading && allSorted.length === 0 && effectiveLens === 'innbygger' ? (
                    <EmptyState
                        icon={<InfoIcon size={32} />}
                        title="Ingen veiledningspunkter er synlige for innbyggeren ennå"
                    />
                ) : !loading && allSorted.length === 0 ? (
                    <EmptyState
                        icon={<InfoIcon size={32} />}
                        title="Ingen veiledningspunkter ennå"
                        action={
                            onAdd ? (
                                <Button variant="secondary" onClick={() => setEditing(null)}>
                                    <PlusIcon size={14} aria-hidden="true" />
                                    Legg til veiledning
                                </Button>
                            ) : undefined
                        }
                    />
                ) : null}
                {allSorted.length > 0 && (
                    <SortableList>
                        {topItems.length > 0 && (middleItems.length + bottomItems.length > 0) && (
                            <li className={styles.tierLabel} role="presentation">
                                <span className={styles.tierLabelIcon}>
                                    <PushPinIcon size={14} aria-hidden="true" />
                                </span>
                                Vises øverst for innbygger
                            </li>
                        )}
                        {topItems.map((s, i) => {
                            const itemProps = topSortable.getItemProps(i);
                            return (
                                <SectionItem
                                    key={s.objectId}
                                    ref={itemProps.ref}
                                    style={itemProps.style}
                                    seksjon={s}
                                    index={i + 1}
                                    expanded={expandedIds.has(s.objectId)}
                                    onExpandedChange={(expanded) => handleExpandedChange(s.objectId, expanded)}
                                    dragHandleProps={dragEnabled ? topSortable.getDragHandleProps(i) : undefined}
                                    isDragging={dragEnabled && topSortable.state.dragIndex === i}
                                    onEdit={setEditing}
                                    onUpdate={handleItemUpdate}
                                    onDelete={onDelete}
                                    onToggleOverride={onToggleOverride}
                                    onHide={onHide}
                                    onUnhide={onUnhide}
                                    isImported={s.globalId ? importedGlobalIds?.has(s.globalId) : false}
                                    kanMerkeUregulert={kanMerkeUregulert}
                                />
                            );
                        })}
                        {middleItems.map((s, i) => {
                            const itemProps = middleSortable.getItemProps(i);
                            return (
                                <SectionItem
                                    key={s.objectId}
                                    ref={itemProps.ref}
                                    style={itemProps.style}
                                    seksjon={s}
                                    index={topItems.length + i + 1}
                                    expanded={expandedIds.has(s.objectId)}
                                    onExpandedChange={(expanded) => handleExpandedChange(s.objectId, expanded)}
                                    dragHandleProps={dragEnabled ? middleSortable.getDragHandleProps(i) : undefined}
                                    isDragging={dragEnabled && middleSortable.state.dragIndex === i}
                                    onEdit={setEditing}
                                    onUpdate={handleItemUpdate}
                                    onDelete={onDelete}
                                    onToggleOverride={onToggleOverride}
                                    onHide={onHide}
                                    onUnhide={onUnhide}
                                    isImported={s.globalId ? importedGlobalIds?.has(s.globalId) : false}
                                    kanMerkeUregulert={kanMerkeUregulert}
                                />
                            );
                        })}
                        {bottomItems.length > 0 && (topItems.length + middleItems.length > 0) && (
                            <li className={styles.tierLabel} role="presentation">
                                <span className={styles.tierLabelIcon}>
                                    <PushPinIcon
                                        size={14}
                                        aria-hidden="true"
                                        className={styles.pinBottom}
                                    />
                                </span>
                                Vises nederst for innbygger
                            </li>
                        )}
                        {bottomItems.map((s, i) => {
                            const itemProps = bottomSortable.getItemProps(i);
                            return (
                                <SectionItem
                                    key={s.objectId}
                                    ref={itemProps.ref}
                                    style={itemProps.style}
                                    seksjon={s}
                                    index={topItems.length + middleItems.length + i + 1}
                                    expanded={expandedIds.has(s.objectId)}
                                    onExpandedChange={(expanded) => handleExpandedChange(s.objectId, expanded)}
                                    dragHandleProps={dragEnabled ? bottomSortable.getDragHandleProps(i) : undefined}
                                    isDragging={dragEnabled && bottomSortable.state.dragIndex === i}
                                    onEdit={setEditing}
                                    onUpdate={handleItemUpdate}
                                    onDelete={onDelete}
                                    onToggleOverride={onToggleOverride}
                                    onHide={onHide}
                                    onUnhide={onUnhide}
                                    isImported={s.globalId ? importedGlobalIds?.has(s.globalId) : false}
                                    kanMerkeUregulert={kanMerkeUregulert}
                                />
                            );
                        })}
                    </SortableList>
                )}
            </div>

            <SectionEditorPage
                open={editing !== undefined}
                seksjon={editing ?? undefined}
                contextLabel={contextLabel}
                onSave={handleSave}
                onClose={() => setEditing(undefined)}
                isPublished={isPublished}
                onPublishAfterSave={onPublishAfterSave}
            />
        </div>
    );
}
